// Breadcrumb model for the shell's top bar (task 355.7): readable labels instead of raw URL segments.
//   - document type / category codes → their names from the (cached) document type tree;
//   - ids (GUIDs) → a short mono "#1a2b3c4d";
//   - section segments without a page of their own (documents, gl, reports, tools, ops, admin) are
//     not links.
//   - /gl/transactions/:id (task 486) has no list page of its own: its crumb is the Ledger (task 489 — the link
//     went to an unknown route, which lands on the dashboard).

import type { DocumentTypeTreeResponse } from '../../../api/generated/schema';

export interface Crumb {
  label: string;
  /** Null when the segment has no page of its own (or is the current page). */
  path: string | null;
  mono?: boolean;
}

const LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  documents: 'Data hub',
  customers: 'Customers',
  tasks: 'My tasks',
  gl: 'General ledger',
  accounts: 'Accounts',
  balances: 'Balances',
  ledger: 'Ledger',
  'transaction-types': 'Transaction types',
  reports: 'Reports',
  'balance-sheet': 'Balance sheet',
  'profit-loss': 'Profit & loss',
  'trial-balance': 'Trial balance',
  'trial-balance-accounts': 'Trial balance by account',
  holds: 'Active holds',
  safeguarding: 'Safeguarding',
  'capital-requirements': 'Capital requirements',
  tools: 'Tools',
  treasury: 'Treasury',
  'fx-position': 'FX position',
  'balance-calculation': 'Balance calculation',
  'fx-rates': 'FX rates',
  fees: 'Fees',
  plans: 'Pricing plans',
  schedules: 'Fee schedules',
  simulator: 'Fee simulator',
  workflows: 'Workflows',
  reference: 'Reference data',
  settings: 'Settings',
  tenant: 'Tenant settings',
  'account-codes': 'Account codes',
  'card-product-routing': 'Card product routing',
  'report-mappings': 'Report mappings',
  limits: 'Transaction limits',
  'workflow-policies': 'Workflow policies',
  ops: 'Operations',
  integration: 'Integration engine',
  dlq: 'Dead-letter queue',
  connectors: 'Connectors',
  clearbank: 'ClearBank settings',
  requests: 'Request audit',
  webhooks: 'Webhooks',
  reconciliation: 'Reconciliation',
  'tenant-flags': 'Tenant flags',
  admin: 'Admin',
  'schema-studio': 'Schema Studio',
  'import-profiles': 'Import profiles',
  'chart-of-accounts': 'Chart of accounts',
  users: 'Users',
  groups: 'Groups',
  roles: 'Roles',
  tenants: 'Tenants',
  new: 'New',
};

/** Segments that group pages but are not a page themselves. */
const NOT_A_PAGE = new Set(['documents', 'gl', 'reports', 'tools', 'treasury', 'ops', 'admin']);

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function typeName(tree: DocumentTypeTreeResponse | undefined, code: string): string | null {
  for (const cat of tree?.categories ?? []) {
    const dt = cat.documentTypes.find((d) => d.typeCode === code);
    if (dt) return dt.typeName;
  }
  return null;
}

function categoryName(tree: DocumentTypeTreeResponse | undefined, code: string): string | null {
  return tree?.categories.find((c) => c.categoryCode === code)?.categoryName ?? null;
}

export function buildCrumbs(pathname: string, tree?: DocumentTypeTreeResponse): Crumb[] {
  const segments = pathname.split('/').filter(Boolean);
  const crumbs: Crumb[] = [];
  let path = '';

  segments.forEach((segment, idx) => {
    path += `/${segment}`;
    const prev = segments[idx - 1];
    const underDocuments = segments[0] === 'documents';

    // /documents/category/:code — "category" is routing, not a crumb.
    if (underDocuments && segment === 'category') return;
    // …and the aggregated list is the Payments shortcut, which sits outside Data hub (task 381).
    if (underDocuments && idx === 0 && segments[1] === 'category') return;

    let crumb: Crumb;
    if (prev === 'gl' && segment === 'transactions') {
      crumb = { label: LABELS.ledger, path: '/gl/ledger' };
    } else if (GUID.test(segment)) {
      crumb = { label: `#${segment.slice(0, 8)}`, path, mono: true };
    } else if (underDocuments && prev === 'category') {
      crumb = { label: `All ${categoryName(tree, segment) ?? segment}`, path };
    } else if (underDocuments && idx === 1) {
      crumb = { label: typeName(tree, segment) ?? segment, path };
    } else {
      crumb = { label: LABELS[segment] ?? segment, path: NOT_A_PAGE.has(segment) ? null : path };
    }
    crumbs.push(crumb);
  });

  if (crumbs.length > 0) crumbs[crumbs.length - 1] = { ...crumbs[crumbs.length - 1], path: null };
  return crumbs;
}
